import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Check,
  HelpCircle,
  ListChecks,
  MessageSquare,
  Sparkles,
  Star,
  Trash,
} from "lucide-react";
import {
  listBookmarks,
  listVisits,
  removeBookmark,
  updateBookmarkNote,
} from "../lib/journey";
import type { BookmarkRecord, VisitRecord } from "../lib/db";
import { setJourneyLimit, useSettings } from "../lib/settings";
import { getExperience, getIndustry, getProject, getTechnology } from "../lib/queries";
import { FEATURES } from "../lib/features";
import { openAsk } from "./ChatPanel";
import { FeedbackForm } from "./Feedback";

type Tab = "analytics" | "bookmarks" | "features" | "feedback";

export function JourneyPanel() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("analytics");
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  const [bookmarks, setBookmarks] = useState<BookmarkRecord[]>([]);
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();
  const { journeyLimit, triedFeatures } = useSettings();
  const panelRef = useRef<HTMLElement>(null);
  const trigRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const on = () => setTick((n) => n + 1);
    window.addEventListener("journey-change", on);
    const id = window.setInterval(on, 4000);
    return () => {
      window.removeEventListener("journey-change", on);
      window.clearInterval(id);
    };
  }, []);

  // While the bug picker is active the panel steps aside: hidden so it doesn't occlude pick
  // targets (the picker also suppresses the outside-mousedown that would otherwise close it).
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || trigRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [v, b] = await Promise.all([listVisits(journeyLimit), listBookmarks()]);
      if (cancelled) return;
      setVisits(v);
      setBookmarks(b);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tick, journeyLimit]);

  const analytics = useMemo(() => computeJourneyAnalytics(visits, bookmarks), [visits, bookmarks]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  const bump = () => setTick((n) => n + 1);

  return (
    <>
      <button
        ref={trigRef}
        type="button"
        className={`journey-fab ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <BarChart3 size={16} />
        <span>{open ? "Close journey" : "Your journey"}</span>
      </button>

      {open && (
        <aside ref={panelRef} data-c="JourneyPanel" style={picking ? { visibility: "hidden" } : undefined} className="journey-panel" role="complementary" aria-label="Your journey">
          <div className="journey-ask-row">
            <div className="grow">
              <div className="journey-title">Your journey</div>
              <div className="journey-sub mono">Private · tracked on this device</div>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm journey-ask-btn"
              onClick={() => {
                setOpen(false);
                openAsk();
              }}
            >
              <Sparkles size={15} /> Ask AI
            </button>
          </div>

          <header className="journey-tabs">
            <JTab
              active={tab === "analytics"}
              onClick={() => setTab("analytics")}
              icon={<BarChart3 size={14} />}
              label="Analytics"
            />
            <JTab
              active={tab === "bookmarks"}
              onClick={() => setTab("bookmarks")}
              icon={<Star size={14} />}
              label={`Bookmarks · ${bookmarks.length}`}
            />
            <JTab
              active={tab === "features"}
              onClick={() => setTab("features")}
              icon={<ListChecks size={14} />}
              label={`Features · ${FEATURES.filter((f) => triedFeatures.includes(f.id)).length}/${FEATURES.length}`}
            />
          </header>

          <div className="journey-body">
            {tab === "analytics" && <JourneyAnalytics a={analytics} />}
            {tab === "bookmarks" && <JourneyBookmarks bookmarks={bookmarks} go={go} bump={bump} />}
            {tab === "features" && <JourneyFeatures triedFeatures={triedFeatures} />}
            {tab === "feedback" && <FeedbackForm onDone={() => setTab("analytics")} onPickingChange={setPicking} />}
          </div>

          <footer className="journey-foot">
            {tab === "feedback" ? (
              <div className="journey-note">
                Feedback is stored locally in this demo. Nothing leaves your browser.
              </div>
            ) : (
              <>
                <button type="button" className="journey-feedback-link" onClick={() => setTab("feedback")}>
                  <MessageSquare size={14} /> Send feedback
                </button>
                <div className="journey-foot-row">
                  <label htmlFor="jlimit">Show last</label>
                  <input
                    id="jlimit"
                    type="range"
                    min={10}
                    max={200}
                    step={10}
                    value={journeyLimit}
                    onChange={(e) => void setJourneyLimit(parseInt(e.target.value, 10))}
                  />
                  <span className="mono journey-foot-num">{journeyLimit}</span>
                </div>
                <div className="journey-note">Stored locally on your device. Nothing leaves your browser.</div>
              </>
            )}
          </footer>
        </aside>
      )}
    </>
  );
}

function JTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button type="button" className={`journey-tab ${active ? "active" : ""}`} onClick={onClick}>
      {icon}
      <span className="journey-tab-label">{label}</span>
    </button>
  );
}

interface JourneyAnalyticsData {
  totalVisits: number;
  uniquePaths: number;
  projectsVisited: number;
  experiencesVisited: number;
  industriesVisited: { id: string; label: string; ts: number }[];
  technologiesVisited: { id: string; label: string; ts: number }[];
  bookmarksCount: number;
  notesCount: number;
}

function computeJourneyAnalytics(
  visits: VisitRecord[],
  bookmarks: BookmarkRecord[],
): JourneyAnalyticsData {
  const seenProjects = new Set<string>();
  const seenExperiences = new Set<string>();
  const indRecency = new Map<string, number>();
  const techRecency = new Map<string, number>();
  const note = (m: Map<string, number>, ids: string[] | undefined, ts: number) =>
    ids?.forEach((id) => {
      if (!m.has(id)) m.set(id, ts);
    });

  // visits arrive newest-first; first sighting wins for recency.
  for (const v of visits) {
    if (v.nodeType === "project" && v.nodeId) {
      seenProjects.add(v.nodeId);
      const p = getProject(v.nodeId);
      note(indRecency, p?.industries, v.ts);
      note(techRecency, p?.technologies, v.ts);
    }
    if (v.nodeType === "experience" && v.nodeId) {
      seenExperiences.add(v.nodeId);
      const e = getExperience(v.nodeId);
      note(indRecency, e?.industries, v.ts);
      note(techRecency, e?.technologies, v.ts);
    }
    if (v.nodeType === "lens" && v.nodeId && v.filters) {
      const params = new URLSearchParams(v.filters);
      if (v.nodeId === "industry") note(indRecency, params.getAll("industry"), v.ts);
      if (v.nodeId === "technology") note(techRecency, params.getAll("tech"), v.ts);
    }
  }

  const ind = Array.from(indRecency.entries())
    .map(([id, ts]) => ({ id, label: getIndustry(id)?.label ?? id, ts }))
    .sort((a, b) => b.ts - a.ts);
  const tech = Array.from(techRecency.entries())
    .map(([id, ts]) => ({ id, label: getTechnology(id)?.label ?? id, ts }))
    .sort((a, b) => b.ts - a.ts);

  return {
    totalVisits: visits.length,
    uniquePaths: new Set(visits.map((v) => v.path)).size,
    projectsVisited: seenProjects.size,
    experiencesVisited: seenExperiences.size,
    industriesVisited: ind,
    technologiesVisited: tech,
    bookmarksCount: bookmarks.length,
    notesCount: bookmarks.filter((b) => (b.note ?? "").trim().length > 0).length,
  };
}

function jFmtTs(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function JourneyAnalytics({ a }: { a: JourneyAnalyticsData }) {
  const metrics = [
    { v: a.totalVisits, l: "Visits" },
    { v: a.uniquePaths, l: "Unique pages" },
    { v: a.projectsVisited, l: "Projects seen" },
    { v: a.experiencesVisited, l: "Companies seen" },
    { v: a.bookmarksCount, l: "Bookmarks" },
    { v: a.notesCount, l: "Notes" },
  ];

  return (
    <div className="journey-analytics">
      <div className="journey-metrics">
        {metrics.map((m) => (
          <div className="journey-metric" key={m.l}>
            <div className="display journey-metric-v">{m.v}</div>
            <div className="journey-metric-l">{m.l}</div>
          </div>
        ))}
      </div>
      <JSection title="Last 3 industries explored">
        {a.industriesVisited.length === 0 ? (
          <JEmpty>none yet</JEmpty>
        ) : (
          <ul className="journey-recents">
            {a.industriesVisited.slice(0, 3).map((i) => (
              <li key={i.id}>
                {i.label} <span className="journey-ago">· {jFmtTs(i.ts)}</span>
              </li>
            ))}
          </ul>
        )}
      </JSection>
      <JSection title="Last 5 technologies explored">
        {a.technologiesVisited.length === 0 ? (
          <JEmpty>none yet</JEmpty>
        ) : (
          <ul className="journey-recents">
            {a.technologiesVisited.slice(0, 5).map((t) => (
              <li key={t.id}>
                {t.label} <span className="journey-ago">· {jFmtTs(t.ts)}</span>
              </li>
            ))}
          </ul>
        )}
      </JSection>
      <JSection title="How to read this">
        <div className="journey-hint">
          Browse projects, companies, and filters — your trail builds here. Star anything to bookmark it
          for later.
        </div>
      </JSection>
    </div>
  );
}

function JourneyBookmarks({
  bookmarks,
  go,
  bump,
}: {
  bookmarks: BookmarkRecord[];
  go: (to: string) => void;
  bump: () => void;
}) {
  if (bookmarks.length === 0)
    return <JEmpty pad>Use the star on any company or engagement to bookmark it for later.</JEmpty>;
  return (
    <ul className="journey-bm-list">
      {bookmarks.map((b) => (
        <li className="journey-bm" key={b.nodeType + b.nodeId}>
          <button type="button" className="journey-bm-title" onClick={() => go(bmHref(b))}>
            {b.title}
          </button>
          <div className="journey-bm-meta mono">
            {b.nodeType} · {jFmtTs(b.ts)}
          </div>
          <textarea
            className="journey-bm-note"
            defaultValue={b.note ?? ""}
            placeholder="Add a private note…"
            rows={2}
            onBlur={(e) => {
              if (e.target.value !== (b.note ?? "")) {
                void updateBookmarkNote(b.id!, e.target.value).then(bump);
              }
            }}
          />
          <button
            type="button"
            className="journey-bm-remove"
            onClick={() => void removeBookmark(b.id!).then(bump)}
          >
            <Trash size={13} /> Remove
          </button>
        </li>
      ))}
    </ul>
  );
}

function JourneyFeatures({ triedFeatures }: { triedFeatures: string[] }) {
  const tried = new Set(triedFeatures);
  const done = FEATURES.filter((f) => tried.has(f.id)).length;
  return (
    <>
      <div className="journey-hist-top">
        <span className="mono journey-ago">{done}/{FEATURES.length} tried</span>
        {done < FEATURES.length && (
          <span className="mono muted">{FEATURES.length - done} to explore</span>
        )}
      </div>
      <ul className="journey-feat-list">
        {FEATURES.map((f) => {
          const isDone = tried.has(f.id);
          return (
            <li key={f.id} className={`journey-feat ${isDone ? "done" : ""}`}>
              <span className="journey-feat-check" aria-hidden="true">
                {isDone && <Check size={12} />}
              </span>
              <span className="journey-feat-label">{f.label}</span>
              <span className="journey-feat-help" tabIndex={0} role="note" aria-label={f.tip} data-tip={f.tip}>
                <HelpCircle size={14} />
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function bmHref(b: BookmarkRecord): string {
  if (b.nodeType === "experience") return `/experience/${b.nodeId}`;
  if (b.nodeType === "project") return `/project/${b.nodeId}`;
  if (b.nodeType === "lens") return `/by/${b.nodeId}`;
  if (b.nodeType === "graph") return "/graph";
  if (b.nodeType === "print") return "/print";
  return "/";
}

function JSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="journey-section">
      <div className="journey-section-h">{title}</div>
      {children}
    </div>
  );
}

function JEmpty({ children, pad }: { children: React.ReactNode; pad?: boolean }) {
  return <div className={`journey-empty ${pad ? "pad" : ""}`}>{children}</div>;
}
